export const MV_TEMPERATURE_MIN_FAHRENHEIT = -40;
export const MV_TEMPERATURE_MAX_FAHRENHEIT = 140;
export const MV_TEMPERATURE_MIN_VELOCITY_FPS = 500;
export const MV_TEMPERATURE_MAX_VELOCITY_FPS = 5000;
export const MV_TEMPERATURE_MAX_ROWS = 32;
export const MV_TEMPERATURE_DEFAULT_FAHRENHEIT = 59;
export const MV_TEMPERATURE_DEFAULT_VELOCITY_FPS = 3000;

const NUMBER = "[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)(?:[eE][+-]?\\d+)?";
const PAIR = new RegExp(`^(${NUMBER})\\s*(?:->|→|:|=)\\s*(${NUMBER})$`);
const FINITE_NUMBER = new RegExp(`^${NUMBER}$`);

function finiteNumber(value, label) {
  if ((typeof value !== "number" && typeof value !== "string")
    || (typeof value === "string" && !FINITE_NUMBER.test(value.trim()))) {
    throw new TypeError(`${label} must be a finite number.`);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) throw new TypeError(`${label} must be a finite number.`);
  return numeric;
}

function serializedNumber(value) {
  const numeric = finiteNumber(value, "Each temperature and muzzle velocity");
  const rounded = Number(numeric.toPrecision(12));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function validateRowCount(rows) {
  if (rows.length === 0) {
    throw new RangeError("Enter at least one known temperature and muzzle velocity.");
  }
  if (rows.length > MV_TEMPERATURE_MAX_ROWS) {
    throw new RangeError(`Enter no more than ${MV_TEMPERATURE_MAX_ROWS} temperature rows.`);
  }
}

function validatedRow(row) {
  if (row === null || typeof row !== "object") {
    throw new TypeError("Each row must contain a temperature and muzzle velocity.");
  }
  const temperatureFahrenheit = finiteNumber(
    row.temperatureFahrenheit, "Each temperature",
  );
  const velocityFps = finiteNumber(row.velocityFps, "Each muzzle velocity");
  if (temperatureFahrenheit < MV_TEMPERATURE_MIN_FAHRENHEIT
    || temperatureFahrenheit > MV_TEMPERATURE_MAX_FAHRENHEIT) {
    throw new RangeError(
      `Each temperature must be between ${MV_TEMPERATURE_MIN_FAHRENHEIT} °F and ${MV_TEMPERATURE_MAX_FAHRENHEIT} °F.`,
    );
  }
  if (velocityFps < MV_TEMPERATURE_MIN_VELOCITY_FPS
    || velocityFps > MV_TEMPERATURE_MAX_VELOCITY_FPS) {
    throw new RangeError(
      `Each muzzle velocity must be between ${MV_TEMPERATURE_MIN_VELOCITY_FPS.toLocaleString("en-US")} and ${MV_TEMPERATURE_MAX_VELOCITY_FPS.toLocaleString("en-US")} ft/s.`,
    );
  }
  return { temperatureFahrenheit, velocityFps };
}

function validateUniqueTemperatures(rows) {
  if (new Set(rows.map(({ temperatureFahrenheit }) => (
    temperatureFahrenheit
  ))).size !== rows.length) {
    throw new RangeError("Enter each temperature only once.");
  }
}

export function parseMvTemperatureTable(value) {
  const text = String(value ?? "").trim();
  if (!text) {
    throw new RangeError("Enter at least one known temperature and muzzle velocity.");
  }
  const entries = text.split(/[\n;,]+/).map((entry) => entry.trim()).filter(Boolean);
  validateRowCount(entries);
  const rows = entries.map((entry) => {
    const match = entry.match(PAIR);
    if (!match) {
      throw new TypeError(
        "Enter each row as “temperature -> muzzle velocity”, one row per line.",
      );
    }
    return validatedRow({
      temperatureFahrenheit: match[1],
      velocityFps: match[2],
    });
  });
  validateUniqueTemperatures(rows);
  return rows;
}

export function normalizeMvTemperatureTable(rows) {
  if (!Array.isArray(rows)) {
    throw new TypeError("The muzzle-velocity temperature table must be a list of rows.");
  }
  validateRowCount(rows);
  const normalized = rows.map(validatedRow);
  validateUniqueTemperatures(normalized);
  return normalized.sort((left, right) => (
    left.temperatureFahrenheit - right.temperatureFahrenheit
  ));
}

export function serializeMvTemperatureTable(rows) {
  return normalizeMvTemperatureTable(rows).map(({ temperatureFahrenheit, velocityFps }) => (
    `${serializedNumber(temperatureFahrenheit)}:${serializedNumber(velocityFps)}`
  )).join(";");
}

export function formatMvTemperatureTable(rows, formatTemperature = String,
  formatVelocity = String) {
  return normalizeMvTemperatureTable(rows).map(({ temperatureFahrenheit, velocityFps }) => (
    `${formatTemperature(temperatureFahrenheit)} → ${formatVelocity(velocityFps)}`
  )).join("\n");
}

function normalizedTable(table) {
  return normalizeMvTemperatureTable(
    typeof table === "string" ? parseMvTemperatureTable(table) : table,
  );
}

function boundedVelocity(velocityFps) {
  if (velocityFps < MV_TEMPERATURE_MIN_VELOCITY_FPS
    || velocityFps > MV_TEMPERATURE_MAX_VELOCITY_FPS) {
    throw new RangeError(
      "The temperature-adjusted muzzle velocity must remain between 500 and 5,000 ft/s.",
    );
  }
  return velocityFps;
}

export function resolveMuzzleVelocity(table, temperatureFahrenheit,
  sensitivityFpsPerFahrenheit) {
  const rows = normalizedTable(table);
  const temperature = finiteNumber(temperatureFahrenheit, "Temperature");
  const sensitivity = finiteNumber(
    sensitivityFpsPerFahrenheit, "Muzzle-velocity temperature sensitivity",
  );
  if (temperature < MV_TEMPERATURE_MIN_FAHRENHEIT
    || temperature > MV_TEMPERATURE_MAX_FAHRENHEIT) {
    throw new RangeError(
      `Temperature must be between ${MV_TEMPERATURE_MIN_FAHRENHEIT} °F and ${MV_TEMPERATURE_MAX_FAHRENHEIT} °F.`,
    );
  }

  const exact = rows.find((row) => row.temperatureFahrenheit === temperature);
  if (exact) return exact.velocityFps;

  for (let index = 1; index < rows.length; index += 1) {
    const lower = rows[index - 1];
    const upper = rows[index];
    if (temperature > lower.temperatureFahrenheit
      && temperature < upper.temperatureFahrenheit) {
      const fraction = (temperature - lower.temperatureFahrenheit)
        / (upper.temperatureFahrenheit - lower.temperatureFahrenheit);
      return boundedVelocity(
        lower.velocityFps + fraction * (upper.velocityFps - lower.velocityFps),
      );
    }
  }

  const anchor = temperature < rows[0].temperatureFahrenheit
    ? rows[0]
    : rows[rows.length - 1];
  return boundedVelocity(
    anchor.velocityFps
      + sensitivity * (temperature - anchor.temperatureFahrenheit),
  );
}

export function migrateLegacyMvTemperatureTable(
  muzzleVelocityFps = MV_TEMPERATURE_DEFAULT_VELOCITY_FPS,
  referenceTemperatureFahrenheit = MV_TEMPERATURE_DEFAULT_FAHRENHEIT,
) {
  return serializeMvTemperatureTable([{
    temperatureFahrenheit: referenceTemperatureFahrenheit,
    velocityFps: muzzleVelocityFps,
  }]);
}
